import * as React from "react";
import {VeBoxCard} from "../../components/card/VeBoxCard";
import {Button, Icon, icons, InputAddon, TextInput, Toggle, Tooltip} from "@vebox/etui-core";
import {
    VeboxSelectionList,
    VeboxSelectionListOptionCheckboxPosition
} from "../../components/selection_list/VeboxSelectionList";
import {generic_value, VeboxModels} from "../../types/veboxModels";
import {useUserDetails} from "../contexts/UserDetailsContext";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {BUTTON_STYLE} from "../../components/common/etui_components_styles";
import {Loader} from "../../components/Utils/loader/Loader";
import {
    disableRole,
    getDifferentRole,
    getDisableMessage,
    getRoleFromRoleCode,
    isManageableRole,
    isRoleMatchingWord,
    ROLE_ADMIN,
    ROLE_END_USER_ADMIN,
    RoleListOption,
    smartRoleSelection
} from "../../components/Utils/RolesUtils";
import {Display} from "../../components/Display";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {DATA_STATE} from "../contexts/UsersDashboardContext";
import {useUserHierarchyVisibility} from "../contexts/UserVisibilityHierarchiesContext";
import {VeboxExpandable} from "../../components/expadable/VeboxExpandable";
import {SearchIcon} from "../components/SearchIcon";
import {ETUI_ICONS_SIZE} from "../UserManagement";
import {useCommonData} from "../contexts/CommonDataProvider";
import {RestDataStatus} from "../../hooks/useRestData";
import {ErrorType} from "../UserDetails";

declare var AppTranslations: AppTranslations;

type UserRolesProps = {
    setError: (errorType: ErrorType) => void
    readOnly: boolean
    isCurrentUser: boolean
    availableRoles: string[]
}

// @ts-ignore
const isFirefox = typeof InstallTrigger !== 'undefined';

const collapsed = (expanded, setExpanded) => {
    const show = () => setExpanded(true);
    const hide = () => setExpanded(false);
    return (
        <div className={"font-weight-400 flx-row flx-space-between flx-align-center"}>
            <div style={{marginRight: isFirefox ? "10px" : "21px"}} onMouseOver={show} onMouseOut={hide}>
                <Icon glyph={icons.INFO_CIRCLE}/>
            </div>
        </div>
    );
}

export const UserRoles: React.FC<UserRolesProps> = (
    {setError, readOnly, isCurrentUser, availableRoles}
) => {

    const [, , , updateUserVisibilityHierarchies] = useUserHierarchyVisibility();
    const {roles: visibleRoles, roleStatus: visibleRolesState} = useCommonData();
    const [manageableRoles, setManageableRoles] = React.useState<VeboxModels.Role[]>([]);
    const [manageableRolesState, setManageableRolesState] = React.useState<DATA_STATE>(DATA_STATE.isLoading);

    const [currentUser, setCurrentUser] = useUserDetails();
    const [CUNormalRoles, setCUNormalRoles] = React.useState<VeboxModels.Role[]>([]);
    const [CUAdminRole, setCUAdminRole] = React.useState<VeboxModels.Role>();


    const [normalRolesSelected, setNormalRolesSelected] = React.useState<generic_value[]>([]);
    const [adminRoleSelected, setAdminRoleSelected] = React.useState<generic_value>();
    const [rolesChanged, setRolesChanged] = React.useState<boolean>(false);
    const [searchWord, setSearchWord] = React.useState("");

    const [userRolesLoaded, setUserRolesLoaded] = React.useState<boolean>(false);
    const [dataState, setDataState] = React.useState<DATA_STATE>(DATA_STATE.isLoading);

    const [isEndUserAdminChecked, setIsEndUserAdminCheck] = React.useState<boolean>(false);
    const [isAdminChecked, setIsAdminCheck] = React.useState<boolean>(false);
    const [disableRoleEndUserAdmin, setDisableRoleEndUserAdmin] = React.useState<boolean>(false);
    const [disableRoleUserAdmin, setDisableRoleUserAdmin] = React.useState<boolean>(false);

    const [isSubmittable, setIsSubmittable] = React.useState<boolean>(true);
    const [showSearchRoleBar, setShowSearchRoleBar] = React.useState<boolean>(false);

    const searchRoleBarRef = React.useRef(null)

    const clearSearchWord = () => setSearchWord("");

    const changeSearchWord = (e: React.ChangeEvent<HTMLInputElement>) => setSearchWord(e.target.value);

    const toggleIsEndUserAdminChecked = () => {
        return () => {
            if (!isEndUserAdminChecked) {
                setAdminRoleSelected(ROLE_END_USER_ADMIN);
                setNormalRolesSelected([]);
            } else {
                setAdminRoleSelected(undefined);
                setIsAdminCheck(false);
                if (CUNormalRoles.length > 0) {
                    setNormalRolesSelected(CUNormalRoles.map((role) => role.code));
                }
            }
            setIsEndUserAdminCheck(!isEndUserAdminChecked);
        }
    };

    const toggleIsRoleAdminChecked = () => {
        return () => {
            if (!isAdminChecked) {
                setAdminRoleSelected(ROLE_ADMIN);
            } else {
                setAdminRoleSelected(ROLE_END_USER_ADMIN);
            }
            setIsAdminCheck(!isAdminChecked);
        }
    };

    const setErrorState = () => {
        setError(ErrorType.UPDATING_ERROR)
        setDataState(DATA_STATE.error);
    }

    const setCurrentUserRoles = () => {
        const normalRoles: VeboxModels.Role[] = currentUser.rolesAndPermissions.filter(
            (role) => role.code !== ROLE_ADMIN && role.code !== ROLE_END_USER_ADMIN);
        setCUNormalRoles(normalRoles);
        setNormalRolesSelected(normalRoles.map((role) => role.code));
        const admRole: VeboxModels.Role = currentUser.rolesAndPermissions.find(
            (role) => role.code === ROLE_ADMIN || role.code === ROLE_END_USER_ADMIN);
        setCUAdminRole(admRole);
        setAdminRoleSelected(admRole?.code);

        setIsEndUserAdminCheck(admRole?.code === ROLE_END_USER_ADMIN || admRole?.code === ROLE_ADMIN);
        setIsAdminCheck(admRole?.code === ROLE_ADMIN);
        setUserRolesLoaded(true);
        setManageableRolesState(DATA_STATE.loaded);
    }

    const checkIsSubmittable = (): boolean => {
        return normalRolesSelected.length > 0 || adminRoleSelected !== undefined;
    }

    const checkRoles = (): boolean => {
        if (normalRolesSelected.length === CUNormalRoles.length && adminRoleSelected === CUAdminRole?.code) {
            for (const elem of normalRolesSelected) {
                if (CUNormalRoles.find((role) => elem === role.code) === undefined) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }

    const updateNormalSelectedRoles = (roles) => {
        const rolesMngmt = visibleRoles.filter((role) => role.code !== ROLE_END_USER_ADMIN && role.code !== ROLE_ADMIN)
        setNormalRolesSelected((prevState) => {
            return smartRoleSelection(
                getDifferentRole((roles as string[]), (prevState as string[])),
                (roles as string[]),
                rolesMngmt)
        });
    }

    const handleCancelNewRoles = () => {
        setCurrentUserRoles();
    }

    const submitNewRules = () => {
        setUserRolesLoaded(false);
        setUserRoles(currentUser.guid, normalRolesSelected, adminRoleSelected)
            .then((newUser: VeboxModels.User) => {
                const hasCurrentUserAdminRoles = currentUser.rolesAndPermissions.find(
                    (r) => r.code === ROLE_ADMIN || r.code === ROLE_END_USER_ADMIN);
                // updating visibility displayed only when is needed
                if (adminRoleSelected && !hasCurrentUserAdminRoles) {
                    updateUserVisibilityHierarchies();
                } else if (normalRolesSelected.length > 0 && hasCurrentUserAdminRoles) {
                    updateUserVisibilityHierarchies();
                }
                setCurrentUser(newUser);
            })
            .catch(() => {
                setErrorState();
            });
    }

    const showHideSearchRoleBar = () => {
        setShowSearchRoleBar(prevState => {
            if (prevState) {
                clearSearchWord()
            }
            return !prevState
        });
    }

    const renderBack = (): JSX.Element => {
        return (
            <div className={"icons-container"}>
                <SearchIcon
                    showHideSearchBar={showHideSearchRoleBar}
                    openSearchBarMessage={AppTranslations.filters.filterBy}
                />
                <Display isVisible={isCurrentUser || readOnly}>
                    <Tooltip
                        message={isCurrentUser
                            ? AppTranslations.usersManagement.fieldsDisabledForHimselfMessage
                            : AppTranslations.usersManagement.fieldsDisabledForReaOnlyUserMessage}
                        placement={"left"}
                    >
                        <Icon glyph={icons.INFO_CIRCLE} size={ETUI_ICONS_SIZE}/>
                    </Tooltip>
                </Display>
            </div>
        )
    }

    const renderVisibleRoles = () => {
        return visibleRoles
            .filter((role) => role.code !== ROLE_END_USER_ADMIN && role.code !== ROLE_ADMIN)
            .filter(isRoleMatchingWord(searchWord))
            .sort((a, b) => a.name > b.name ? 1 : -1)
            .map((role) => {
                const isDisabled: boolean = disableRole(role, visibleRoles, manageableRoles,
                    normalRolesSelected, isEndUserAdminChecked);
                return <RoleListOption
                    role={role}
                    key={role.code}
                    disabledMessage={
                        getDisableMessage(role,
                            isDisabled,
                            manageableRoles,
                            AppTranslations.usersManagement.roleContainedInAnother,
                            AppTranslations.usersManagement.roleNotAvailableMessage)}
                    disable={isDisabled}
                />
            });
    }

    // Show/hide searchBar
    React.useEffect(() => {
        if (searchRoleBarRef && showSearchRoleBar) {
            searchRoleBarRef.current.focus();
        }
    }, [showSearchRoleBar])

    React.useEffect(() => {
        if (visibleRolesState === RestDataStatus.LOADED && userRolesLoaded && manageableRolesState === DATA_STATE.loaded && manageableRoles) {
            setRolesChanged(!checkRoles());
            if (checkIsSubmittable()) {
                setIsSubmittable(true);
            } else {
                setIsSubmittable(false);
            }
        }
    }, [normalRolesSelected, adminRoleSelected])

    React.useEffect(() => {
        setRolesChanged(false);
    }, [CUNormalRoles, CUAdminRole])

    React.useEffect(() => {
        if (userRolesLoaded) {
            const mngRoles: VeboxModels.Role[] = availableRoles.map(role => getRoleFromRoleCode(role, visibleRoles));
            setManageableRoles(mngRoles)
            setDisableRoleUserAdmin(readOnly
                || !isManageableRole(getRoleFromRoleCode(ROLE_ADMIN, visibleRoles), mngRoles))
            setDisableRoleEndUserAdmin(readOnly
                || !isManageableRole(getRoleFromRoleCode(ROLE_END_USER_ADMIN, visibleRoles), mngRoles))
        }
    }, [availableRoles, userRolesLoaded])

    React.useEffect(() => {
        setUserRolesLoaded(false);
        if (visibleRolesState === RestDataStatus.LOADED) {
            setCurrentUserRoles();
        }
    }, [JSON.stringify(currentUser.rolesAndPermissions), visibleRolesState])

    React.useEffect(() => {
        setDataState((userRolesLoaded && manageableRolesState === DATA_STATE.loaded)
            ? DATA_STATE.loaded
            : DATA_STATE.isLoading)
    }, [userRolesLoaded, manageableRolesState])

    return (
        <>
            <VeBoxCard className={"Section"}>
                <VeBoxCard.Header
                    title={AppTranslations.userCreationProcess.rolesStageTitle.toUpperCase()}
                    singleLineTitle={true}
                    back={renderBack()}
                />
                <VeBoxCard.Body className={"detail-card"}>
                    <Display isVisible={dataState === DATA_STATE.isLoading}>
                        <Loader/>
                    </Display>
                    <Display isVisible={dataState === DATA_STATE.loaded}>
                        <TextInput
                            elRef={searchRoleBarRef}
                            label={AppTranslations.filters.filterBy}
                            styles={{
                                input: {boxSizing: "border-box"},
                                root: {display: (!showSearchRoleBar ? "none" : "")}
                            }}
                            className={"margin-bottom-10"}
                            value={searchWord}
                            onChange={changeSearchWord}
                            endInputAddon={
                                <InputAddon
                                    position={"end"}
                                >
                                    {Boolean(searchWord) && <span
                                        className="material-icons-outlined clickable"
                                        onClick={clearSearchWord}
                                    >clear</span>}
                                </InputAddon>
                            }
                        />
                        <Display
                            isVisible={visibleRoles
                                .filter(isRoleMatchingWord(searchWord))
                                .find(role => role.code === ROLE_END_USER_ADMIN)}
                        >
                            <div className={"row margin-bottom-10 position-relative"}>
                                <Tooltip
                                    message={getDisableMessage(getRoleFromRoleCode(ROLE_END_USER_ADMIN, visibleRoles),
                                        disableRoleEndUserAdmin,
                                        manageableRoles,
                                        AppTranslations.usersManagement.roleContainedInAnother,
                                        AppTranslations.usersManagement.roleNotAvailableMessage)}
                                    placement={"right"}
                                    disableToolTip={!disableRoleEndUserAdmin}
                                >
                                    <Toggle
                                        name={"role_end_user_admin"}
                                        onChange={toggleIsEndUserAdminChecked()}
                                        isChecked={isEndUserAdminChecked}
                                        isDisabled={disableRoleEndUserAdmin}
                                        renderLabels={renderLabelFactory(
                                            AppTranslations.userCreationProcess.enableAdminPermissions)}
                                    />
                                </Tooltip>
                                <VeboxExpandable
                                    key={ROLE_END_USER_ADMIN}
                                    collapsed={collapsed}
                                >
                                    <div
                                        className={"bottom-tooltip " + (isFirefox ? "" : "margin-right-11-after")}
                                        style={{marginTop: 35}}
                                    >
                                        {AppTranslations.usersManagement.roleEndUserAdminDescription}
                                    </div>
                                </VeboxExpandable>
                            </div>
                        </Display>
                        <Display
                            isVisible={visibleRoles
                                .filter(isRoleMatchingWord(searchWord))
                                .find(role => role.code === ROLE_ADMIN) && isEndUserAdminChecked}
                        >
                            <div className={"row margin-bottom-10"}>
                                <Tooltip
                                    message={getDisableMessage(getRoleFromRoleCode(ROLE_ADMIN, visibleRoles),
                                        disableRoleUserAdmin,
                                        manageableRoles,
                                        AppTranslations.usersManagement.roleContainedInAnother,
                                        AppTranslations.usersManagement.roleNotAvailableMessage)}
                                    placement={"right"}
                                    disableToolTip={!disableRoleUserAdmin}
                                >
                                    <Toggle
                                        name={"role_admin"}
                                        onChange={toggleIsRoleAdminChecked()}
                                        isChecked={isAdminChecked}
                                        isDisabled={disableRoleUserAdmin}
                                        renderLabels={renderLabelFactory(
                                            AppTranslations.userCreationProcess.giveFullAccess)}
                                    />
                                </Tooltip>

                            </div>
                            <div className={"padding-5"}>
                                {AppTranslations.userCreationProcess.adminUserCreationWarning}
                            </div>
                        </Display>
                        <div className={"users-management-list-container"}>
                            <VeboxSelectionList
                                className={"selection-list users-management-list"}
                                checkboxPosition={VeboxSelectionListOptionCheckboxPosition.START}
                                values={normalRolesSelected}
                                onChange={updateNormalSelectedRoles}
                                disable={isEndUserAdminChecked || readOnly}
                            >
                                {renderVisibleRoles()}
                            </VeboxSelectionList>
                        </div>
                        <Display isVisible={rolesChanged}>
                            <div className={"user-details-buttons-section"}>
                                <Button
                                    onClick={handleCancelNewRoles}
                                    variant="tertiary"
                                    style={{minWidth: 70}}
                                    styles={BUTTON_STYLE}
                                >
                                    {AppTranslations.genericWords.cancel}
                                </Button>
                                <Tooltip
                                    message={AppTranslations.usersManagement.errorUserWithoutRoles}
                                    disableToolTip={isSubmittable}
                                    placement={"top"}
                                >
                                    <Button
                                        onClick={submitNewRules}
                                        variant="strong"
                                        style={{minWidth: 70}}
                                        styles={BUTTON_STYLE}
                                        disabled={!isSubmittable}
                                    >
                                        {AppTranslations.genericWords.submit}
                                    </Button>
                                </Tooltip>
                            </div>
                        </Display>
                    </Display>
                </VeBoxCard.Body>
            </VeBoxCard>
        </>
    )
}

const renderLabelFactory = (ifTrue: string,
                            ifFalse?: string) => (isChecked: boolean) => isChecked ? ifTrue : (ifFalse ?? ifTrue);

function setUserRoles(userGuid: string, normalRolesSelected: generic_value[], adminRoleSelected: generic_value) {
    return new Promise<VeboxModels.User>((res, rej) => {
        if (adminRoleSelected) {
            RestDataLoader.putJSON(
                "users/" + userGuid + "/roles",
                {
                    set: [adminRoleSelected]
                },
                (newUser: VeboxModels.User) => {
                    res(newUser);
                },
                rej
            )
        } else {
            RestDataLoader.putJSON(
                "users/" + userGuid + "/roles",
                {
                    set: normalRolesSelected
                },
                (newUser: VeboxModels.User) => {
                    res(newUser);
                },
                rej
            )
        }

    })
}